import {ApptSlot} from "@services/monolith/availability";
import React from "react";

import NoSlots from "./NoSlots";
import SlotGrid from "./SlotGrid";
import SlotGroupBorder from "./SlotGroupBorder";

/**
 * A switch-like component that decides how to present a group of slots based on input.
 */
const SlotGroup = ({
  dist,
  withBorder,
  onSlotSelection,
  selectedSlotTime,
  slots,
  timezone,
  title,
  loading,
  disabled,
}: {
  /**
   * Callback called when a user selects a slot
   * @param slot The slot that the user selected
   */
  onSlotSelection: (slot: ApptSlot) => unknown;
  /**
   * The time of an already selected slot which is used to highlight it.
   */
  selectedSlotTime?: number;
  /**
   * Slots to render
   */
  slots: ApptSlot[];
  /**
   * The timezone of the location associated with the slots.
   */
  timezone: string;
  /**
   * When true, the group will render a set of slot placeholders.
   */
  loading?: boolean;
  /**
   * When true, the slot buttons will be disabled.
   */
  disabled?: boolean;
} & (
  | {
      /**
       * Whether to add a border around the group with a title line.
       * If true, you should add a title to the group and optionally, a distance.
       */
      withBorder: true;
      /**
       * The title of the location used to render the border
       */
      title: string;
      /**
       * The distance in miles to a location
       */
      dist?: number;
    }
  | {
      /**
       * Whether to add a border around the group with a title line.
       * If not, you do not need a title.
       */
      withBorder?: false;
      title?: undefined;
      dist?: undefined;
    }
)) => {
  if (slots && !slots.length && !withBorder && !loading) {
    return <NoSlots />;
  }

  if (withBorder) {
    return (
      <SlotGroupBorder name={title} distance={dist}>
        <SlotGrid
          onSlotSelection={onSlotSelection}
          selectedSlotTime={selectedSlotTime}
          slots={slots}
          timezone={timezone}
          loading={loading}
          disabled={disabled}
        />
      </SlotGroupBorder>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 w-full py-4">
      <SlotGrid
        onSlotSelection={onSlotSelection}
        selectedSlotTime={selectedSlotTime}
        slots={slots}
        timezone={timezone}
        loading={loading}
        disabled={disabled}
      />
    </div>
  );
};

export default SlotGroup;
